import { KeyRound, LogOut, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { maskPhone } from '@core/phone';
import { currencySymbol, SUPPORTED_CURRENCIES } from '@core/money';
import { pinProblem } from '@core/pin';

import { Button, Chip, useToast } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useApp } from '@/lib/hooks';
import { changePin, deleteAccount, signOut, updateProfile } from '@/lib/store';

export function Settings() {
  const { user } = useApp();
  const toast = useToast();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [pins, setPins] = useState({ current: '', next: '', confirm: '' });
  const [pinError, setPinError] = useState<string>();
  const [busy, setBusy] = useState<string | null>(null);
  if (!user) return null;

  const run = async (key: string, task: () => Promise<void>) => {
    setBusy(key);
    try {
      await task();
    } catch (e) {
      toast({ message: e instanceof Error ? e.message : 'Something went wrong' });
    } finally {
      setBusy(null);
    }
  };

  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 6);

  return (
    <div className="page" style={{ maxWidth: 760 }}>
      <div className="page-head"><h1>Settings</h1></div>
      <div className="grid">
        <section className="card grid">
          <div className="card-head" style={{ margin: 0 }}><h2>Profile</h2><span className="muted">{maskPhone(user.phone)}</span></div>
          <div className="row" style={{ gap: 12 }}>
            <label className="field" style={{ flex: 1 }}><span>First name</span><input className="input" value={firstName} maxLength={40} onChange={(e) => setFirstName(e.target.value)} /></label>
            <label className="field" style={{ flex: 1 }}><span>Last name</span><input className="input" value={lastName} maxLength={40} onChange={(e) => setLastName(e.target.value)} /></label>
          </div>
          <div>
            <Button loading={busy === 'profile'} disabled={!firstName.trim()} onClick={() => run('profile', async () => { await updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() }); toast({ message: 'Profile saved' }); })}>Save profile</Button>
          </div>
        </section>

        <section className="card grid">
          <div className="card-head" style={{ margin: 0 }}><h2>Currency</h2></div>
          <p className="muted" style={{ margin: 0 }}>Changes how amounts are shown on the web. Existing amounts are not converted.</p>
          <div className="chips">
            {SUPPORTED_CURRENCIES.map((c) => (
              <Chip key={c} on={user.currency === c} onClick={() => run('currency', () => updateProfile({ currency: c }))}>{currencySymbol(c)} {c}</Chip>
            ))}
          </div>
        </section>

        <section className="card grid">
          <div className="card-head" style={{ margin: 0 }}><h2>Change PIN</h2></div>
          <form className="grid" onSubmit={(e) => {
            e.preventDefault();
            const problem = pinProblem(pins.next);
            if (problem) return setPinError(problem);
            if (pins.next !== pins.confirm) return setPinError("New PINs don't match");
            void run('pin', async () => {
              try {
                await changePin(pins.current, pins.next);
                setPins({ current: '', next: '', confirm: '' });
                setPinError(undefined);
                toast({ message: 'PIN changed' });
              } catch (err) {
                setPinError(err instanceof ApiError ? err.message : 'Something went wrong');
              }
            });
          }}>
            <div className="row wrap" style={{ gap: 12 }}>
              {(['current', 'next', 'confirm'] as const).map((k) => (
                <label key={k} className="field" style={{ flex: 1, minWidth: 150 }}>
                  <span>{k === 'current' ? 'Current PIN' : k === 'next' ? 'New PIN' : 'Confirm new PIN'}</span>
                  <input className="input pin-input" style={{ fontSize: 20, letterSpacing: 8 }} type="password" inputMode="numeric" maxLength={6}
                    autoComplete={k === 'current' ? 'current-password' : 'new-password'}
                    value={pins[k]} onChange={(e) => (setPins((p) => ({ ...p, [k]: digits(e.target.value) })), setPinError(undefined))} />
                </label>
              ))}
            </div>
            {pinError && <span className="error-text">{pinError}</span>}
            <div><Button icon={KeyRound} type="submit" loading={busy === 'pin'} disabled={Object.values(pins).some((p) => p.length !== 6)}>Change PIN</Button></div>
          </form>
        </section>

        <section className="card grid">
          <div className="card-head" style={{ margin: 0 }}><h2>Account</h2></div>
          <div className="row wrap">
            <Button variant="secondary" icon={LogOut} onClick={() => run('out', signOut)}>Sign out</Button>
            <Button variant="ghost" icon={Trash2} style={{ color: 'var(--danger)' }} onClick={() => {
              if (confirm('Delete your Spendly account? All synced expenses and receipts are deleted from the server. Data on your phone is kept.')) {
                void run('delete', deleteAccount);
              }
            }}>Delete account</Button>
          </div>
        </section>
      </div>
    </div>
  );
}
